import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { BlockInstance, WebsitePage } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import {
  useWebsite,
  useUpdateWebsite,
  usePublishWebsite,
  useUnpublishWebsite,
} from '../hooks/useWebsites';
import { useModel } from '../hooks/useModels';
import { useDatabases } from '@/modules/databases/hooks/useDatabases';
import { PageSeoEditor } from '../components/PageSeoEditor';
import { CustomizerPanel } from '../components/customizer/CustomizerPanel';
import { PanelRoot } from '../components/customizer/PanelRoot';
import { PanelGlobalGroup } from '../components/customizer/PanelGlobalGroup';
import { PanelPages } from '../components/customizer/PanelPages';
import { PanelPage } from '../components/customizer/PanelPage';
import { PanelBlock } from '../components/customizer/PanelBlock';
import { PanelDatabases } from '../components/customizer/PanelDatabases';
import { PanelDomains } from '../components/customizer/PanelDomains';
import { PanelAddBlock } from '../components/customizer/PanelAddBlock';
import { PanelChooseLayout } from '../components/customizer/PanelChooseLayout';
import { PreviewFrame } from '../components/customizer/PreviewFrame';
import { ApplyToPagesModal } from '../components/customizer/ApplyToPagesModal';
import { applyPreset, presetsForPage } from '../components/customizer/applyPreset';
import {
  canAddBlock,
  getBlockName,
  getPageName,
  sortBlocksByOrder,
  type ApplyCandidate,
  type PanelLevel,
} from '../components/customizer/types';

/** Edição de website em layout customizer: painel drill-down (360px) + preview iframe. */
export const WebsiteCustomizerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: website, isLoading } = useWebsite(id);
  const { data: model } = useModel(website?.modelId);
  const updateMutation = useUpdateWebsite();
  const publishMutation = usePublishWebsite();
  const unpublishMutation = useUnpublishWebsite();
  const { data: databases } = useDatabases({ limit: 100 });

  // ----- Estado do formulário (mesmo pattern da página anterior) -----
  const [name, setName] = useState('');
  const [globalConfig, setGlobalConfig] = useState<Record<string, unknown>>({});
  const [pages, setPages] = useState<WebsitePage[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [databaseIds, setDatabaseIds] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // ----- Estado do customizer -----
  const [stack, setStack] = useState<PanelLevel[]>([{ kind: 'root' }]);
  const [previewPageId, setPreviewPageId] = useState<string>('');
  // #408: no mobile, painel e preview não cabem lado a lado — alterna entre eles.
  const [mobileView, setMobileView] = useState<'panel' | 'preview'>('panel');
  const [reloadToken, setReloadToken] = useState(0);
  const [scrollTarget, setScrollTarget] = useState<{ pageId: string; instanceId: string; token: number } | null>(null);
  /** Campos alterados na sessão, por bloco: `${pageId}|${instanceId}` -> fieldNames. */
  const [dirtyFields, setDirtyFields] = useState<Record<string, string[]>>({});
  const [applyCandidates, setApplyCandidates] = useState<ApplyCandidate[] | null>(null);

  useEffect(() => {
    if (website) {
      setName(website.name);
      setGlobalConfig(website.globalConfig || {});
      setPages(website.pages || []);
      setDomains(website.domains || []);
      setDatabaseIds(website.databaseIds || []);
      setHasChanges(false);
    }
  }, [website]);

  // Reset do estado do customizer ao trocar de website (o componente não remonta)
  useEffect(() => {
    setStack([{ kind: 'root' }]);
    setPreviewPageId('');
    setDirtyFields({});
    setApplyCandidates(null);
    setScrollTarget(null);
    setReloadToken(0);
  }, [id]);

  useEffect(() => {
    if (pages.length > 0 && !previewPageId) setPreviewPageId(pages[0].pageId);
  }, [pages, previewPageId]);

  const markChanged = () => setHasChanges(true);
  const current = stack[stack.length - 1];
  const push = (level: PanelLevel) => setStack((s) => [...s, level]);
  const pop = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  // Breadcrumb: volta direto para um nível ancestral (trilha do drill-down, sem seta de voltar).
  const goToLevel = (index: number) =>
    setStack((s) => (index >= 0 && index < s.length - 1 ? s.slice(0, index + 1) : s));

  // ----- Grupos da config global (dinâmico do schema, na ordem de aparição) -----
  const globalGroups = useMemo(() => {
    const groups: string[] = [];
    for (const field of model?.globalConfigSchema || []) {
      const g = field.group || 'Geral';
      if (!groups.includes(g)) groups.push(g);
    }
    return groups;
  }, [model]);

  // ----- Mutators -----
  const updateBlock = (pageId: string, instanceId: string, updater: (b: BlockInstance) => BlockInstance) => {
    setPages((prev) =>
      prev.map((p) =>
        p.pageId !== pageId ? p : { ...p, blocks: p.blocks.map((b) => (b.instanceId === instanceId ? updater(b) : b)) }
      )
    );
    markChanged();
  };

  const handleBlockFieldChange = (pageId: string, instanceId: string, fieldName: string, value: unknown) => {
    updateBlock(pageId, instanceId, (b) => ({ ...b, config: { ...b.config, [fieldName]: value } }));
    setDirtyFields((prev) => {
      const key = `${pageId}|${instanceId}`;
      const fields = prev[key] || [];
      return fields.includes(fieldName) ? prev : { ...prev, [key]: [...fields, fieldName] };
    });
  };

  const handleToggleVisible = (pageId: string, instanceId: string) => {
    updateBlock(pageId, instanceId, (b) => ({ ...b, visible: !b.visible }));
  };

  const handleMoveBlock = (pageId: string, instanceId: string, direction: 'up' | 'down') => {
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageId !== pageId) return p;
        const sorted = sortBlocksByOrder(p.blocks);
        const idx = sorted.findIndex((b) => b.instanceId === instanceId);
        const swap = direction === 'up' ? idx - 1 : idx + 1;
        if (idx < 0 || swap < 0 || swap >= sorted.length) return p;
        [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
        return { ...p, blocks: sorted.map((b, i) => ({ ...b, order: i + 1 })) };
      })
    );
    markChanged();
  };

  const handleAddBlock = (pageId: string, blockId: string) => {
    const blockDef = model?.blocks.find((b) => b.id === blockId);
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageId !== pageId) return p;
        const newBlock: BlockInstance = {
          instanceId: crypto.randomUUID(),
          blockId,
          config: { ...(blockDef?.defaultConfig ?? {}) },
          order: 0, // renormalizado abaixo (entra no fim)
          visible: true,
          addedByUser: true,
        };
        // Renormaliza order para 1..N (consistente com duplicar/remover); o novo bloco vai ao fim.
        const next = [...sortBlocksByOrder(p.blocks), newBlock];
        return { ...p, blocks: next.map((b, i) => ({ ...b, order: i + 1 })) };
      })
    );
    markChanged();
    pop(); // volta do catálogo para o nível da página
  };

  const handleDuplicateBlock = (pageId: string, instanceId: string) => {
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageId !== pageId) return p;
        const sorted = sortBlocksByOrder(p.blocks);
        const idx = sorted.findIndex((b) => b.instanceId === instanceId);
        if (idx < 0) return p;
        const source = sorted[idx];
        if (!canAddBlock(model, p, source.blockId)) return p;
        // Cópia independente: novo id, config snapshot, sem mirrorOf, marcada como do usuário.
        const { mirrorOf: _drop, ...rest } = source;
        const dup: BlockInstance = {
          ...rest,
          instanceId: crypto.randomUUID(),
          config: { ...source.config },
          visible: true,
          addedByUser: true,
          order: source.order, // recalculado abaixo
        };
        const next = [...sorted.slice(0, idx + 1), dup, ...sorted.slice(idx + 1)];
        return { ...p, blocks: next.map((b, i) => ({ ...b, order: i + 1 })) };
      })
    );
    markChanged();
  };

  const handleRemoveBlock = (pageId: string, instanceId: string) => {
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageId !== pageId) return p;
        const target = p.blocks.find((b) => b.instanceId === instanceId);
        if (!target?.addedByUser) return p; // só remove blocos do usuário
        const kept = sortBlocksByOrder(p.blocks.filter((b) => b.instanceId !== instanceId));
        return { ...p, blocks: kept.map((b, i) => ({ ...b, order: i + 1 })) };
      })
    );
    // Se o painel estava aberto exatamente neste bloco, volta um nível.
    setStack((s) => {
      const top = s[s.length - 1];
      return top.kind === 'block' && top.instanceId === instanceId && s.length > 1 ? s.slice(0, -1) : s;
    });
    markChanged();
  };

  const handleMirrorChange = (pageId: string, instanceId: string, value: string) => {
    if (value === 'custom') {
      updateBlock(pageId, instanceId, (b) => {
        const { mirrorOf: _unlinked, ...rest } = b;
        return rest; // mantém o config atual (snapshot hidratado) como base de edição
      });
      return;
    }
    setPages((prev) => {
      const sourcePage = prev.find((p) => p.pageId === value);
      return prev.map((p) =>
        p.pageId !== pageId
          ? p
          : {
              ...p,
              blocks: p.blocks.map((b) => {
                if (b.instanceId !== instanceId) return b;
                const source = sourcePage?.blocks.find((sb) => sb.blockId === b.blockId);
                // Copia o config da origem localmente (snapshot) — o backend re-hidrata em todo read
                return { ...b, mirrorOf: value, config: source ? { ...source.config } : b.config };
              }),
            }
      );
    });
    markChanged();
  };

  const handlePageChange = (pageId: string, updatedPage: WebsitePage) => {
    setPages((prev) => prev.map((p) => (p.pageId === pageId ? updatedPage : p)));
    markChanged();
  };

  // ----- "Aplicar nas outras páginas?" -----
  const computeApplyCandidates = (): ApplyCandidate[] => {
    const out: ApplyCandidate[] = [];
    for (const [key, fields] of Object.entries(dirtyFields)) {
      if (fields.length === 0) continue;
      const [pageId, instanceId] = key.split('|');
      const page = pages.find((p) => p.pageId === pageId);
      const blockInstance = page?.blocks.find((b) => b.instanceId === instanceId);
      if (!page || !blockInstance || blockInstance.mirrorOf) continue;
      const targets = pages
        .filter((p) => p.pageId !== pageId)
        .filter((p) => p.blocks.some((b) => b.blockId === blockInstance.blockId && !b.mirrorOf))
        .map((p) => ({ pageId: p.pageId, pageName: getPageName(model, p.pageId) }));
      if (targets.length === 0) continue;
      const blockDef = model?.blocks.find((b) => b.id === blockInstance.blockId);
      out.push({
        key,
        blockId: blockInstance.blockId,
        sourceInstanceId: blockInstance.instanceId,
        blockName: getBlockName(model, blockInstance.blockId),
        sourcePageId: pageId,
        sourcePageName: getPageName(model, pageId),
        fields,
        fieldLabels: fields.map((f) => blockDef?.configSchema.find((c) => c.name === f)?.label || f),
        targets,
      });
    }
    return out;
  };

  const buildPagesWithApplied = (selection: Record<string, string[]>): WebsitePage[] => {
    let next = pages;
    for (const cand of applyCandidates || []) {
      const targetIds = selection[cand.key] || [];
      if (targetIds.length === 0) continue;
      const sourcePage = next.find((p) => p.pageId === cand.sourcePageId);
      const sourceBlock = sourcePage?.blocks.find((b) => b.instanceId === cand.sourceInstanceId);
      if (!sourceBlock) continue;
      next = next.map((p) => {
        if (!targetIds.includes(p.pageId)) return p;
        return {
          ...p,
          blocks: p.blocks.map((b) => {
            if (b.blockId !== cand.blockId || b.mirrorOf) return b;
            const copied: Record<string, unknown> = { ...b.config };
            for (const f of cand.fields) copied[f] = sourceBlock.config[f];
            return { ...b, config: copied };
          }),
        };
      });
    }
    return next;
  };

  // ----- Save / Publish -----
  const persist = async (pagesToSave: WebsitePage[]): Promise<boolean> => {
    if (!id) return false;
    try {
      await updateMutation.mutateAsync({
        id,
        data: { name, globalConfig, pages: pagesToSave, domains, databaseIds },
      });
      setPages(pagesToSave);
      setHasChanges(false);
      setDirtyFields({});
      setApplyCandidates(null);
      setReloadToken((t) => t + 1); // recarrega o preview
      return true;
    } catch {
      // Erro toastado pelo interceptor do axios (api.ts)
      return false;
    }
  };

  const handleSave = async () => {
    const candidates = computeApplyCandidates();
    if (candidates.length > 0) {
      setApplyCandidates(candidates);
      return;
    }
    await persist(pages);
  };

  const handlePublish = async () => {
    if (!id) return;
    if (hasChanges) {
      // Publica o que está na tela — replicação multi-página fica para o Salvar explícito
      const ok = await persist(pages);
      if (!ok) return;
    }
    publishMutation.mutate(id);
  };

  const handleUnpublish = async () => {
    if (!id) return;
    if (hasChanges) {
      const ok = await persist(pages);
      if (!ok) return;
    }
    unpublishMutation.mutate(id);
  };

  // ----- Navegação que sincroniza o preview -----
  const openPage = (pageId: string) => {
    push({ kind: 'page', pageId });
    setPreviewPageId(pageId);
  };

  const openBlock = (pageId: string, instanceId: string) => {
    push({ kind: 'block', pageId, instanceId });
    setPreviewPageId(pageId);
    setScrollTarget({ pageId, instanceId, token: Date.now() });
  };

  const getLevelTitle = (level: PanelLevel): string => {
    switch (level.kind) {
      case 'root': return 'Personalizar site';
      case 'global-group': return level.group;
      case 'pages': return 'Páginas';
      case 'page': return getPageName(model, level.pageId);
      case 'page-seo': return `SEO — ${getPageName(model, level.pageId)}`;
      case 'block': {
        const blk = pages.find((p) => p.pageId === level.pageId)?.blocks.find((b) => b.instanceId === level.instanceId);
        return blk ? getBlockName(model, blk.blockId) : 'Bloco';
      }
      case 'add-block': return 'Adicionar bloco';
      case 'choose-layout': return 'Escolher layout';
      case 'databases': return 'Bancos de Dados';
      case 'domains': return 'Domínios';
    }
  };

  const renderLevel = (): React.ReactNode => {
    if (!model) return null;
    switch (current.kind) {
      case 'root':
        return (
          <PanelRoot
            name={name}
            onNameChange={(v) => { setName(v); markChanged(); }}
            globalGroups={globalGroups}
            pagesCount={pages.length}
            databasesCount={databaseIds.length}
            domainsCount={domains.length}
            onOpen={push}
          />
        );
      case 'global-group': {
        const fields = model.globalConfigSchema.filter((f) => (f.group || 'Geral') === current.group);
        return (
          <PanelGlobalGroup
            group={current.group}
            fields={fields}
            values={globalConfig}
            onChange={(fieldName, value) => {
              setGlobalConfig((prev) => ({ ...prev, [fieldName]: value }));
              markChanged();
            }}
          />
        );
      }
      case 'pages':
        return <PanelPages pages={pages} model={model} onOpenPage={openPage} />;
      case 'page': {
        const page = pages.find((p) => p.pageId === current.pageId);
        if (!page) return null;
        return (
          <PanelPage
            page={page}
            model={model}
            onOpenSeo={() => push({ kind: 'page-seo', pageId: page.pageId })}
            onOpenBlock={(instanceId) => openBlock(page.pageId, instanceId)}
            onToggleVisible={(instanceId) => handleToggleVisible(page.pageId, instanceId)}
            onMoveBlock={(instanceId, dir) => handleMoveBlock(page.pageId, instanceId, dir)}
            onDuplicateBlock={(instanceId) => handleDuplicateBlock(page.pageId, instanceId)}
            onRemoveBlock={(instanceId) => handleRemoveBlock(page.pageId, instanceId)}
            onOpenAddBlock={() => push({ kind: 'add-block', pageId: page.pageId })}
            hasPresets={presetsForPage(model, page.pageId).length > 0}
            onOpenChooseLayout={() => push({ kind: 'choose-layout', pageId: page.pageId })}
          />
        );
      }
      case 'page-seo': {
        const page = pages.find((p) => p.pageId === current.pageId);
        if (!page) return null;
        return <PageSeoEditor page={page} onChange={(updated) => handlePageChange(page.pageId, updated)} />;
      }
      case 'block': {
        const page = pages.find((p) => p.pageId === current.pageId);
        const blockInstance = page?.blocks.find((b) => b.instanceId === current.instanceId);
        if (!page || !blockInstance) return null;
        const mirrorOptions = pages
          .filter((p) => p.pageId !== page.pageId)
          .filter((p) => p.blocks.some((b) => b.blockId === blockInstance.blockId && !b.mirrorOf))
          .map((p) => ({ label: `Espelhar de ${getPageName(model, p.pageId)}`, value: p.pageId }));
        return (
          <PanelBlock
            block={blockInstance}
            model={model}
            mirrorOptions={mirrorOptions}
            onFieldChange={(fieldName, value) => handleBlockFieldChange(page.pageId, blockInstance.instanceId, fieldName, value)}
            onMirrorChange={(value) => handleMirrorChange(page.pageId, blockInstance.instanceId, value)}
          />
        );
      }
      case 'add-block': {
        const page = pages.find((p) => p.pageId === current.pageId);
        if (!page) return null;
        return (
          <PanelAddBlock
            page={page}
            model={model}
            onAdd={(blockId) => handleAddBlock(page.pageId, blockId)}
          />
        );
      }
      case 'choose-layout': {
        const page = pages.find((p) => p.pageId === current.pageId);
        if (!page) return null;
        return (
          <PanelChooseLayout
            page={page}
            model={model}
            onApply={(preset, applyColors) => {
              setPages((prev) => prev.map((p) => (p.pageId === page.pageId ? applyPreset(p, preset, model) : p)));
              if (applyColors && preset.suggestedGlobalConfig) {
                setGlobalConfig((prev) => ({ ...prev, ...preset.suggestedGlobalConfig }));
              }
              markChanged();
              pop();
            }}
          />
        );
      }
      case 'databases':
        return (
          <PanelDatabases
            databaseIds={databaseIds}
            databases={(databases?.items || []).map((d) => ({ id: d.id, name: d.name }))}
            onAdd={(dbId) => { setDatabaseIds((prev) => [...prev, dbId]); markChanged(); }}
            onRemove={(dbId) => { setDatabaseIds((prev) => prev.filter((x) => x !== dbId)); markChanged(); }}
          />
        );
      case 'domains':
        return (
          <PanelDomains
            domains={domains}
            onAdd={(d) => { setDomains((prev) => [...prev, d]); markChanged(); }}
            onRemove={(d) => { setDomains((prev) => prev.filter((x) => x !== d)); markChanged(); }}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--theme-primary)' }} />
      </div>
    );
  }

  if (!website || !id) {
    return (
      <div className="h-full flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <p style={{ color: 'var(--theme-muted-text)' }}>Website não encontrado</p>
        <button onClick={() => navigate('/websites')} className="mt-4 text-sm underline" style={{ color: 'var(--theme-primary)' }}>
          Voltar para lista
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex relative" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Toggle mobile (#408) — alterna painel/preview (some no md+) */}
      <div
        className="md:hidden fixed bottom-[84px] left-1/2 -translate-x-1/2 z-20 flex gap-1 p-1 rounded-full shadow-lg"
        style={{ background: 'var(--theme-overlay-card-background)', border: '1px solid var(--theme-card-border)' }}
        data-testid="customizer-mobile-toggle"
      >
        {([['panel', 'Editar', 'edit'], ['preview', 'Visualizar', 'eye']] as const).map(([v, label, icon]) => (
          <button
            key={v}
            onClick={() => setMobileView(v)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors"
            style={{
              backgroundColor: mobileView === v ? 'var(--theme-primary)' : 'transparent',
              color: mobileView === v ? '#ffffff' : 'var(--theme-muted-text)',
            }}
            data-testid={`customizer-mobile-${v}`}
          >
            <Icon name={icon} className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Painel drill-down */}
      <div
        className={`${mobileView === 'preview' ? 'hidden md:flex' : 'flex'} w-full md:w-[360px] flex-shrink-0 h-full border-r flex-col`}
        style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
        data-testid="customizer-panel"
      >
        <CustomizerPanel
          siteName={website.name}
          status={website.status}
          hasChanges={hasChanges}
          isSaving={updateMutation.isPending}
          isPublishing={publishMutation.isPending || unpublishMutation.isPending}
          breadcrumb={stack.map((level, i) => ({
            label: getLevelTitle(level),
            onClick: i < stack.length - 1 ? () => goToLevel(i) : undefined,
          }))}
          depth={stack.length}
          onExit={() => navigate('/websites')}
          onSave={() => { void handleSave(); }}
          onPublish={() => { void handlePublish(); }}
          onUnpublish={() => { void handleUnpublish(); }}
        >
          {renderLevel()}
        </CustomizerPanel>
      </div>

      {/* Preview */}
      <div className={`${mobileView === 'panel' ? 'hidden md:block' : 'block'} flex-1 h-full min-w-0`}>
        <PreviewFrame
          websiteId={id}
          pagesDef={model?.pages || []}
          activePageId={previewPageId}
          onPageChange={setPreviewPageId}
          reloadToken={reloadToken}
          scrollTarget={scrollTarget}
        />
      </div>

      {/* Modal "Aplicar nas outras páginas?" */}
      {applyCandidates && applyCandidates.length > 0 && (
        <ApplyToPagesModal
          candidates={applyCandidates}
          onCancel={() => setApplyCandidates(null)}
          onOnlyThisPage={() => { void persist(pages); }}
          onApply={(selection) => { void persist(buildPagesWithApplied(selection)); }}
        />
      )}
    </div>
  );
};
